import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import axios from 'axios';
import './FamilyStyles.css';

/**
 * Family Incoming Video Call Component
 * WebRTC-Videoanruf vom Senior Device zu Family User
 * 
 * Family Member ist der ANSWERER (empfängt Offer, sendet Answer)
 */
function FamilyIncomingVideoCall() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { familyUser } = useFamilyAuth();
  const navigate = useNavigate();
  
  const [callSession, setCallSession] = useState(null);
  const [senior, setSenior] = useState(null);
  const [callState, setCallState] = useState('connecting'); // connecting, in_call, ended, error
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState('good');
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const callTimerRef = useRef(null);
  const containerRef = useRef(null);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  useEffect(() => {
    loadCallSession();
    return () => cleanup();
  }, [sessionId]);

  // Call-Timer starten wenn Call verbunden ist
  useEffect(() => {
    if (callState === 'in_call') {
      setCallDuration(0);
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const loadCallSession = async () => {
    try {
      setCallState('connecting');
      
      // Lade Session-Informationen
      const sessionRes = await axios.get(`${BACKEND_URL}/api/call/session/${sessionId}`);
      const session = sessionRes.data;
      setCallSession(session);
      
      // Lade Senior-Informationen über Device
      const deviceRes = await axios.get(`${BACKEND_URL}/api/devices/${session.device_id}`);
      const device = deviceRes.data;
      
      if (device.senior_id) {
        const seniorsRes = await axios.get(`${BACKEND_URL}/api/households/${familyUser.household_id}/seniors`);
        const seniorData = seniorsRes.data.find(s => s.id === device.senior_id);
        setSenior(seniorData);
      }
      
      // Call annehmen und WebRTC setup starten
      await acceptCall(session);
    } catch (error) {
      console.error('Fehler beim Laden der Call Session:', error);
      setError('Call Session konnte nicht geladen werden');
      setCallState('error');
    }
  };

  const acceptCall = async (session) => {
    try {
      // WebSocket verbinden
      await connectWebSocket(session);
      
      // Kamera/Mikrofon anfordern
      await setupLocalMedia();
      
      // PeerConnection erstellen
      setupPeerConnection();
      
      // Call als akzeptiert markieren via Backend
      await axios.post(`${BACKEND_URL}/api/call/${session.id}/accept`);
      
      // Dem Device mitteilen, dass Call akzeptiert wurde
      if (wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'call_accepted',
          session_id: session.id
        }));
      }
      
    } catch (error) {
      console.error('Fehler beim Annehmen des Calls:', error);
      const errorMsg = error.name === 'NotAllowedError' 
        ? 'Kamera-/Mikrofonzugriff verweigert'
        : error.name === 'NotFoundError'
        ? 'Keine Kamera oder Mikrofon gefunden'
        : 'Fehler beim Einrichten der Verbindung';
      
      setError(errorMsg);
      setCallState('error');
    }
  };

  const connectWebSocket = (session) => {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${WS_URL}/api/ws/call/family/${familyUser.id}`);
      
      ws.onopen = () => {
        console.log('Family WebSocket verbunden (Incoming Call)');
        wsRef.current = ws;
        resolve();
      };
      
      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        console.log('Family empfängt (Incoming):', data);
        
        switch (data.type) {
          case 'sdp_offer':
            // Device sendet Offer, wir müssen Answer erstellen
            console.log('📡 Empfangenes SDP Offer vom Device');
            if (pcRef.current && data.sdp_offer) {
              await pcRef.current.setRemoteDescription(
                new RTCSessionDescription({ type: 'offer', sdp: data.sdp_offer })
              );
              console.log('✅ Remote description (offer) gesetzt');
              
              // Answer erstellen und senden
              await createAndSendAnswer(data.session_id);
            }
            break;
            
          case 'ice_candidate':
            if (pcRef.current && data.candidate) {
              await pcRef.current.addIceCandidate(
                new RTCIceCandidate(data.candidate)
              );
            }
            break;
            
          case 'call_end':
            setCallState('ended');
            setTimeout(() => navigate('/family/dashboard'), 2000);
            break;
            
          default:
            console.log('Unbekannter Message-Typ:', data.type);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket Fehler:', error);
        setError('Verbindungsfehler');
        setCallState('error');
        reject(error);
      };
      
      ws.onclose = () => {
        console.log('Family WebSocket geschlossen (Incoming Call)');
      };
    });
  };

  const setupLocalMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Kamera-/Mikrofonzugriff verweigert:', error);
      throw error;
    }
  };

  const setupPeerConnection = () => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });
    
    // Lokale Tracks hinzufügen
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    // ICE Candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          session_id: sessionId,
          candidate: event.candidate
        }));
      }
    };
    
    // Remote Stream
    pc.ontrack = (event) => {
      console.log('Remote Track empfangen:', event.streams[0]);
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log('ICE Connection State:', pc.iceConnectionState);
      
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setCallState('in_call');
        setConnectionQuality('good');
      } else if (pc.iceConnectionState === 'checking') {
        setConnectionQuality('fair');
      } else if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        setConnectionQuality('poor');
        setError('Verbindung unterbrochen');
        setCallState('error');
      }
    };
    
    pcRef.current = pc;
  };

  const createAndSendAnswer = async (sessionIdParam) => {
    if (!pcRef.current) return;
    
    try {
      const answer = await pcRef.current.createAnswer();
      await pcRef.current.setLocalDescription(answer);
      
      if (wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'sdp_answer',
          session_id: sessionIdParam || sessionId,
          sdp_answer: answer.sdp
        }));
        console.log('✅ SDP Answer gesendet');
      }
    } catch (error) {
      console.error('Fehler beim Erstellen der Answer:', error);
      setError('WebRTC-Verbindung fehlgeschlagen');
      setCallState('error');
    }
  };

  const endCall = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({
        type: 'call_end',
        session_id: sessionId
      }));
    }
    
    cleanup();
    setCallState('ended');
    setTimeout(() => navigate('/family/dashboard'), 1000);
  };

  // UI Control Functions
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const toggleFullscreen = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS && remoteVideoRef.current) {
      if (remoteVideoRef.current.webkitEnterFullscreen) {
        remoteVideoRef.current.webkitEnterFullscreen();
      }
    } else {
      if (!document.fullscreenElement) {
        if (containerRef.current?.requestFullscreen) {
          containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        } else if (containerRef.current?.webkitRequestFullscreen) {
          containerRef.current.webkitRequestFullscreen();
          setIsFullscreen(true);
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    }
  };

  const cleanup = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  // UI Rendering
  return (
    <div className="family-video-call">
      {/* Fehler-Overlay */}
      {callState === 'error' && (
        <div className="family-call-error">
          <div className="family-call-error-content">
            <div className="family-call-error-icon">❌</div>
            <h2>Fehler</h2>
            <p>{error}</p>
            <button 
              onClick={() => navigate('/family/dashboard')}
              className="family-btn-primary"
            >
              Zurück
            </button>
          </div>
        </div>
      )}

      {/* Connecting State */}
      {callState === 'connecting' && (
        <div className="family-call-ringing">
          <div className="family-call-ringing-content">
            {senior?.image_url ? (
              <img src={senior.image_url} alt={senior?.name} className="family-call-avatar" />
            ) : (
              <div className="family-call-avatar-placeholder">
                {senior?.name?.charAt(0) || '👤'}
              </div>
            )}
            <h2>{senior?.name || 'Senior'}</h2>
            <p className="family-call-status">Verbinde...</p>
            <div className="family-call-ringing-animation">
              <div className="family-call-ring"></div>
              <div className="family-call-ring"></div>
              <div className="family-call-ring"></div>
            </div>
          </div>
        </div>
      )}

      {/* In-Call State */}
      {callState === 'in_call' && (
        <div className="family-call-active" ref={containerRef}>
          {/* Remote Video (Groß) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="family-call-remote-video"
          />
          
          {/* Local Video (Klein, Overlay) */}
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="family-call-local-video"
            style={{ display: isVideoOff ? 'none' : 'block' }}
          />
          
          {/* Video Off Placeholder */}
          {isVideoOff && (
            <div className="family-call-local-video-off">
              <div className="family-video-off-icon">📹</div>
            </div>
          )}
          
          {/* Call Info Header */}
          <div className="family-call-header">
            <div className="family-call-info">
              <span className="family-call-name">{senior?.name || 'Senior'}</span>
              <span className="family-call-status-text">
                {`${Math.floor(callDuration / 60)}:${String(callDuration % 60).padStart(2, '0')}`}
              </span>
            </div>
            <div className={`family-connection-indicator family-connection-${connectionQuality}`}>
              <div className="connection-dot"></div>
              {connectionQuality === 'good' && '●●●'}
              {connectionQuality === 'fair' && '●●○'}
              {connectionQuality === 'poor' && '●○○'}
            </div>
          </div>
          
          {/* Call Controls */}
          <div className="family-call-controls">
            <button 
              onClick={toggleMute}
              className={`family-control-btn ${isMuted ? 'active' : ''}`}
              title={isMuted ? 'Stummschaltung aufheben' : 'Stummschalten'}
            >
              {isMuted ? '🔇' : '🎤'}
            </button>
            
            <button 
              onClick={toggleVideo}
              className={`family-control-btn ${isVideoOff ? 'active' : ''}`}
              title={isVideoOff ? 'Video einschalten' : 'Video ausschalten'}
            >
              {isVideoOff ? '📹' : '📷'}
            </button>
            
            <button 
              onClick={endCall}
              className="family-btn-hang-up-active"
            >
              📞
            </button>
            
            <button 
              onClick={toggleFullscreen}
              className="family-control-btn"
              title={isFullscreen ? 'Vollbild verlassen' : 'Vollbild'}
            >
              {isFullscreen ? '⛶' : '⛶'}
            </button>
          </div>
        </div>
      )}

      {/* Ended State */}
      {callState === 'ended' && (
        <div className="family-call-ended">
          <div className="family-call-ended-content">
            <div className="family-call-ended-icon">✓</div>
            <h2>Anruf beendet</h2>
            <p>Wird geschlossen...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default FamilyIncomingVideoCall;
